import React, { useState, useRef, useEffect } from "react";
import MonacoEditor from "react-monaco-editor";
import { Box, Snackbar, Alert } from "@mui/material";
import * as monaco from 'monaco-editor';
import { GraphQLClient, gql } from "graphql-request";
import { buildClientSchema, parse, validate } from "graphql";
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import { useTheme } from "contexts/ThemeContext";

// Define custom theme for the editor
monaco.editor.defineTheme("custom-monaco-theme", {
  base: 'vs',
  inherit: true,
  rules: [
    { token: "key", foreground: "A31515" },
    { token: "value", foreground: "0451A5" },
    { token: "number", foreground: "098658" },
    { token: "boolean", foreground: "FF8C00" },
  ],
  colors: {
    'editor.background': '#ffffff',             // Light mode background
    'editor.foreground': '#282a2c',             // Light mode text
    'editorLineNumber.foreground': '#64748B',   // Light mode line numbers
    'editor.lineHighlightBackground': '#f5f5f5', // Light gray for line highlight
    'editor.lineHighlightBorder': '#e5e5e5',    // Lighter gray for line highlight border
    'editorGutter.background': '#e9e9e9',       // Light mode gutter
    'editor.selectionBackground': '#add6ff',    // Light blue for selection
    'editor.inactiveSelectionBackground': '#e5ebf1', // Light gray for inactive selection
  }
});

// Define dark theme for the editor
monaco.editor.defineTheme("custom-monaco-theme-dark", {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: "key", foreground: "9CDCFE" },
    { token: "value", foreground: "CE9178" },
    { token: "number", foreground: "B5CEA8" },
    { token: "boolean", foreground: "569CD6" },
  ],
  colors: {
    'editor.background': '#0e1525',             // Dark theme background
    'editor.foreground': '#d4d4d4',             // VS Code light gray text
    'editorLineNumber.foreground': '#94a3b8',   // Dark theme line numbers
    'editor.lineHighlightBackground': '#1a2234', // Darker blue for line highlight
    'editor.lineHighlightBorder': '#2a2d2e',    // Dark gray for line highlight border
    'editorGutter.background': '#0e1525',       // Dark theme gutter
    'editor.selectionBackground': '#264f78',     // VS Code selection
    'editor.inactiveSelectionBackground': '#3a3d41', // VS Code inactive selection
  }
});

// Define a new language for JSON
monaco.languages.register({ id: "jsonWithCustomColors" });

monaco.languages.setMonarchTokensProvider("jsonWithCustomColors", {
  tokenizer: {
    root: [
      // Tokenize keys and values in JSON
      [/\"([a-zA-Z0-9_]+)\"\s*:/, { token: "key" }],
      [/\"([^\"\\]*(?:\\.[^\"\\]*)*)\"/, { token: "value" }],
      [/(\d+)/, { token: "number" }],
      [/\b(true|false|null)\b/, { token: "boolean" }],
      [/[{}[\]]/, { token: "delimiter" }],
      [/,/, { token: "delimiter" }],
    ],
  },
});

// Create the BodyGraphQl component
const BodyGraphQl = ({ url, body, headers, onBodyChange }) => {
  const { openSnackbar } = useSnackbar();
  const [bodyRaw, setBodyRaw] = useState("{}");
  const [errors, setErrors] = useState([]);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [introspectionResult, setIntrospectionResult] = useState(null);
  const editorRef = useRef(null);
  const { isDarkMode } = useTheme();

  const introspectionQuery = gql`
    query IntrospectionQuery {
      __schema {
        queryType {
          name
        }
        mutationType {
          name
        }
        types {
          kind
          name
          description
          fields(includeDeprecated: true) {
            name
            description
            args {
              name
              description
              type {
                ...TypeRef
              }
              defaultValue
            }
            type {
              ...TypeRef
            }
            isDeprecated
            deprecationReason
          }
          inputFields {
            name
            description
            type {
              ...TypeRef
            }
            defaultValue
          }
          interfaces {
            ...TypeRef
          }
          enumValues(includeDeprecated: true) {
            name
            description
            isDeprecated
            deprecationReason
          }
          possibleTypes {
            ...TypeRef
          }
        }
      }
    }

    fragment TypeRef on __Type {
      kind
      name
      ofType {
        kind
        name
        ofType {
          kind
          name
          ofType {
            kind
            name
          }
        }
      }
    }
  `;

  const fetchSchema = async () => {
    const headersObject = Object.fromEntries(
      headers.map(h => [h.name, h.value])
    );
    try {
      const client = new GraphQLClient(url, {
        headers: headersObject
      });
      const response = await client.request(introspectionQuery);
      setIntrospectionResult(response);
    } catch (error) {
      openSnackbar({ message: "Invalid GraphQL Endpoint URL", severity: "error" })
      console.error('GraphQL Error:', error);
    }
  }

  useEffect(() => {
    fetchSchema();
  }, [url]);

  useEffect(() => {
    // Handle null or undefined
    if (!body) {
      setBodyRaw("{}");
      return;
    }

    // Handle string input
    if (typeof body === 'string') {
      try {
        const parsedBody = JSON.parse(body);
        setBodyRaw(parsedBody.query || body);
      } catch {
        // If parsing fails, use the string as is
        setBodyRaw(body);
      }
      return;
    }

    // Handle object input
    if (typeof body === 'object') {
      setBodyRaw(body.query || JSON.stringify(body, null, 2));
      return;
    }

    // Fallback for any other type
    setBodyRaw(body);
  }, [body]);

  const handleChange = (newBodyRaw) => {
    setBodyRaw(newBodyRaw);
  };

  const handleOutsideClick = (event) => {
    if (editorRef.current && !editorRef.current.contains(event.target)) {
      try {
        const schema = buildClientSchema(introspectionResult);
        const parsedQuery = parse(bodyRaw);
        const validationErrors = validate(schema, parsedQuery);
        if (validationErrors.length) {
          setErrors(validationErrors);
        } else {
          onBodyChange(bodyRaw);
          setErrors([]);
        }
      } catch (err) {
        console.log(err);
        setErrors([{ message: "Invalid GraphQL format. Please correct it before saving." }]);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  useEffect(() => {
    if (errors?.length > 0) {
      setShowSnackbar(true);
    } else {
      setShowSnackbar(false);
    }
  }, [errors]);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [bodyRaw, introspectionResult]);

  const renderErrors = () => {
    return errors.map((error, index) => (
      <div key={index}>{error.message}</div>
    ));
  };


  return (
    <Box
      ref={editorRef}
      sx={{
        height: "200px",
        overflow: "auto",
        border: "1px solid var(--borderColor)",
        margin: "10px 0",
        borderRadius: "5px",
        position: "relative",
        backgroundColor: "var(--background-primary)",
      }}
    >
      <MonacoEditor
        language="jsonWithCustomColors"
        theme={isDarkMode ? "custom-monaco-theme-dark" : "custom-monaco-theme"}
        value={bodyRaw}
        onChange={handleChange}
        options={{
          automaticLayout: false,
          wordWrap: "on",
          minimap: { enabled: false },
          lineNumbers: "on",
          tabSize: 8,
          fontSize: 11,
          scrollBeyondLastLine: false,
        }}
      />
      <Snackbar
        open={showSnackbar}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="error"
          onClose={handleCloseSnackbar}
        >
          {errors.map((error, index) => (
            <div key={index}>{error.message}</div>
          ))}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BodyGraphQl;
